import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
} from 'react';
import InputMask from 'react-input-mask';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { brDisplayToIso, isoToBrDisplay, normalizeIsoToYyyyMmDd } from '@utils/date/date';
import { FormInputContainer, Input, ErrorMessage } from '../Input/Input.style';
import { DatepickerIcon, ArrowLeftIcon, ArrowRightIcon, ErrorSolidIcon } from '@icons';
import { FormLabel } from '..';
import {
  CalendarHeader,
  CalendarIconButton,
  CalendarNavButton,
  CalendarPopover,
  CalendarTitle,
  DayCell,
  DaysGrid,
  InputAffixWrapper,
  WeekdayCell,
  WeekdayRow,
} from './InputDate.style';

const ISO_DATE = 'yyyy-MM-dd';
const WEEKDAY_LABELS_PT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function mergeRefs<T>(...refs: (React.Ref<T> | undefined)[]) {
  return (instance: T | null) => {
    refs.forEach((r) => {
      if (!r) return;
      if (typeof r === 'function') (r as (x: T | null) => void)(instance);
      else (r as React.MutableRefObject<T | null>).current = instance;
    });
  };
}

export interface FormInputDateProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> {
  errorText?: string;
  label?: string;
  tooltip?: {
    title: string;
    content: string;
  };
  /** ISO: `yyyy-MM-dd` ou ISO 8601 completo (ex.: `2024-03-15T00:00:00.000Z`). */
  value?: string;
  onChange?: (isoValue: string) => void;
}

const FormInputDate = forwardRef<HTMLInputElement, FormInputDateProps>(
  (
    {
      errorText,
      label,
      tooltip,
      id,
      value,
      onChange,
      onBlur,
      name,
      placeholder = 'dd/mm/aaaa',
      disabled,
      readOnly,
      ...rest
    },
    ref
  ) => {
    const [display, setDisplay] = useState(() => isoToBrDisplay(value));
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [viewMonth, setViewMonth] = useState(() => startOfMonth(new Date()));
    const affixRef = useRef<HTMLDivElement>(null);

    const normalizedValue = normalizeIsoToYyyyMmDd(value ?? '');
    const selectedDate =
      normalizedValue && isValid(parse(normalizedValue, ISO_DATE, new Date()))
        ? parse(normalizedValue, ISO_DATE, new Date())
        : null;

    const calendarLocked = !!disabled || !!readOnly;

    useEffect(() => {
      setDisplay((prev) => {
        const parsed = brDisplayToIso(prev);
        const v = value ?? '';
        if (parsed === normalizeIsoToYyyyMmDd(v)) return prev;
        return isoToBrDisplay(v);
      });
    }, [value]);

    useEffect(() => {
      if (!calendarOpen) return;
      const onDocMouseDown = (e: MouseEvent) => {
        if (affixRef.current && !affixRef.current.contains(e.target as Node)) {
          setCalendarOpen(false);
        }
      };
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setCalendarOpen(false);
      };
      document.addEventListener('mousedown', onDocMouseDown);
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.removeEventListener('mousedown', onDocMouseDown);
        document.removeEventListener('keydown', onKeyDown);
      };
    }, [calendarOpen]);

    const openOrToggleCalendar = () => {
      if (calendarLocked) return;
      setCalendarOpen((open) => {
        if (open) return false;
        if (selectedDate) setViewMonth(startOfMonth(selectedDate));
        else setViewMonth(startOfMonth(new Date()));
        return true;
      });
    };

    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const calendarDays = eachDayOfInterval({ start: gridStart, end: gridEnd });

    const pickDay = (day: Date) => {
      if (calendarLocked) return;
      const iso = format(day, ISO_DATE);
      setDisplay(isoToBrDisplay(iso));
      onChange?.(iso);
      setCalendarOpen(false);
    };

    return (
      <FormInputContainer>
        {label && (
          <FormLabel htmlFor={id} tooltip={tooltip}>
            {label}
          </FormLabel>
        )}
        <InputAffixWrapper ref={affixRef}>
          <InputMask
            mask="99/99/9999"
            maskChar={null}
            value={display}
            onChange={(e) => {
              const next = e.target.value;
              setDisplay(next);
              onChange?.(brDisplayToIso(next));
            }}
            onBlur={onBlur}
            disabled={disabled}
            readOnly={readOnly}
          >
            {(
              inputProps: InputHTMLAttributes<HTMLInputElement> & {
                ref?: React.Ref<HTMLInputElement>;
              }
            ) => {
              const { ref: maskRef, ...maskedInputProps } = inputProps;
              return (
                <Input
                  {...maskedInputProps}
                  {...rest}
                  ref={mergeRefs(ref, maskRef)}
                  id={id}
                  name={name}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder={placeholder}
                  hasError={!!errorText}
                  lang="pt-BR"
                  disabled={disabled}
                  readOnly={readOnly}
                />
              );
            }}
          </InputMask>
          <CalendarIconButton
            type="button"
            tabIndex={-1}
            $disabled={calendarLocked}
            disabled={calendarLocked}
            aria-expanded={calendarOpen}
            aria-haspopup="dialog"
            aria-label="Abrir calendário"
            onMouseDown={(e) => e.preventDefault()}
            onClick={openOrToggleCalendar}
          >
            <DatepickerIcon aria-hidden />
          </CalendarIconButton>
          {calendarOpen && (
            <CalendarPopover role="dialog" aria-label="Selecionar data">
              <CalendarHeader>
                <CalendarNavButton
                  type="button"
                  aria-label="Mês anterior"
                  onClick={() => setViewMonth((d) => subMonths(d, 1))}
                >
                  <ArrowLeftIcon aria-hidden />
                </CalendarNavButton>
                <CalendarTitle>
                  {format(viewMonth, 'MMMM yyyy', { locale: ptBR })}
                </CalendarTitle>
                <CalendarNavButton
                  type="button"
                  aria-label="Próximo mês"
                  onClick={() => setViewMonth((d) => addMonths(d, 1))}
                >
                  <ArrowRightIcon aria-hidden />
                </CalendarNavButton>
              </CalendarHeader>
              <WeekdayRow>
                {WEEKDAY_LABELS_PT.map((wd) => (
                  <WeekdayCell key={wd}>{wd}</WeekdayCell>
                ))}
              </WeekdayRow>
              <DaysGrid>
                {calendarDays.map((day) => {
                  const outside = !isSameMonth(day, viewMonth);
                  const selected = selectedDate ? isSameDay(day, selectedDate) : false;
                  return (
                    <DayCell
                      key={format(day, ISO_DATE)}
                      type="button"
                      $outsideMonth={outside}
                      $selected={selected}
                      $isToday={isToday(day)}
                      onClick={() => pickDay(day)}
                    >
                      {format(day, 'd')}
                    </DayCell>
                  );
                })}
              </DaysGrid>
            </CalendarPopover>
          )}
        </InputAffixWrapper>
        {errorText && (
          <ErrorMessage>
            <ErrorSolidIcon /> <span>{errorText}</span>
          </ErrorMessage>
        )}
      </FormInputContainer>
    );
  }
);

FormInputDate.displayName = 'FormInputDate';

export default FormInputDate;
